import { AbstractClient, CompositionClient } from "@/app/client";
import * as types from "@/app/types";
import { UUID } from "crypto";

export interface ProductRelationshipResponse {
  id: UUID;
  parent_id: UUID;
  child_id: UUID;
  created_at: string;
  updated_at: string;
}

export default class APIProductRelationships extends CompositionClient {
  constructor(client: AbstractClient) {
    super(client);
  }

  /**
   * Add a child product to a parent product (create relationship)
   */
  addChild = async (
    parentId: UUID,
    childId: UUID,
  ): Promise<ProductRelationshipResponse> => {
    let res = await this._fetch({
      url: `/products/${parentId}/children`,
      method: "POST",
      params: { child_id: childId },
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  /**
   * Remove a child product from a parent product (delete relationship)
   */
  removeChild = async (parentId: UUID, childId: UUID): Promise<void> => {
    await this._fetch({
      url: `/products/${parentId}/children/${childId}`,
      method: "DELETE",
      auth: ["OAuth2PasswordBearer"],
    });
  };

  /**
   * Get all children (direct or recursive) of a product
   */
  getChildren = async (
    productId: UUID,
    recursive: boolean = false,
  ): Promise<types.ProductResponse[]> => {
    let res = await this._fetch({
      url: `/products/${productId}/children`,
      method: "GET",
      params: { recursive: recursive.toString() },
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  /**
   * Get all parent products of a product
   */
  getParents = async (productId: UUID): Promise<types.ProductResponse[]> => {
    let res = await this._fetch({
      url: `/products/${productId}/parents`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  /**
   * Get full hierarchy tree of a product
   */
  getHierarchy = async (productId: UUID): Promise<types.ProductResponse> => {
    let res = await this._fetch({
      url: `/products/${productId}/hierarchy`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };
}
